import type { ChangeEvent, ComponentProps, KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import type { TerminalResult } from "@/lib/types";
import { cn } from "@/lib/utils";

function TerminalInput({
  className,
  placeholder,
  disabled,
  onEnter,
  outputs,
  ...props
}: ComponentProps<"input"> & {
  placeholder?: string;
  onEnter: (text: string) => void;
  outputs?: TerminalResult[];
}) {
  const [selectedInput, setSelectedInput] = useState<number>(-1);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    setInputValue(outputs?.[selectedInput]?.command ?? "");
  }, [selectedInput, outputs]);

  const handleNavigate = (delta: number) => {
    if (!outputs || outputs.length === 0) return;

    setSelectedInput((prev) =>
      delta > 0
        ? Math.min(outputs.length - 1, prev + 1)
        : Math.max(-1, prev - 1),
    );
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleNavigate(1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleNavigate(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const text = inputValue.trim();

      if (text && onEnter) {
        onEnter(text);
        setInputValue("");
        setSelectedInput(-1);
      }
    }
  };

  return (
    <div className="flex items-center bg-black px-6">
      <span className="text-left">&gt;&nbsp;</span>
      <input
        type="text"
        data-testid="terminal-input"
        className={cn(
          "w-full outline-none py-6",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
        placeholder={placeholder}
        disabled={disabled}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...props}
      />
    </div>
  );
}

export { TerminalInput };
